import Extension from "../base/extension";

class Facets extends Extension {
  form; // The HTML form element of the facets form
  filterData = []; // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial;
  searchParamsPrev;

  /**
   * ID of the section, so we know what section to request via fetch
   * @type {String}
   */
  sectionId;

  constructor(name) {
    super(name);
  }

  /**
   * Initialisation
   */
  init() {
    // Detect changes of radios within this element, as that means variant changed
    // Handle multiple forms (inline and offcanvas) - use querySelectorAll to get all forms
    const forms = document.querySelectorAll("#facets-form");
    if(!forms || forms.length === 0) return;

    // Store reference to all forms
    this.forms = Array.from(forms);
    // Use the first form as primary (for backwards compatibility)
    this.form = this.forms[0];

    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.sectionId = document.querySelector("#products-container").dataset.sectionId;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for form input changes - i.e. the facet inputs. We call the
    // listener after a 'debounce' period to allow the user some time to
    // complete their input (rather than immediately on input)
    this.debouncedOnInput = debounce((event) => {
      this.onFacetsFormInputChange(event);
    }, 500);
    
    // Add listeners to all forms (inline and offcanvas)
    this.forms.forEach((form) => {
      form.addEventListener('input', this.debouncedOnInput.bind(this));

      // Listen for 'facet remove' clicks, trigger the load. We need to listen on 
      // the container, rather than directly on the buttons, as the buttons are
      // re-rendered / dynamically injected each time a filter is applied.
      form.addEventListener('click', function(event) {
        if(event.target.closest("a")?.classList.contains("facet-remove")){
          event.preventDefault();
          let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
          this.onActiveFilterClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
        };
      }.bind(this));
    });
  }

  /**
   * Handle an input change in the facets form, and render the new results
   * @param {Event} e input event 
   */
  onFacetsFormInputChange(e) {
    // Get form data from the form that triggered the event
    const form = e.target.closest('form') || this.form;
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, e);
  }

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    document.getElementById('results-loading-faceted-results').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = element => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {String} filterDataUrl The URL we previously cached
   * @param {Event} event 
   */
  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    document.getElementById('results-loading-faceted-results').classList.add('d-none');
    flashtagLoader();
    Waypoint.refreshAll();
    var event = new CustomEvent("collection:filtered");
    document.dispatchEvent(event);
  }

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        document.getElementById('results-loading-faceted-results').classList.add('d-none');
        flashtagLoader();
        Waypoint.refreshAll();
        var event = new CustomEvent("collection:filtered");
        document.dispatchEvent(event);
      });
  }

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetLiveRegions = parsedHTML.querySelectorAll("[data-live-region^='facet-']");

    Array.from(facetLiveRegions).forEach((element) => {
      // Update ALL elements with the same data-live-region (for both inline and offcanvas)
      const liveRegionValue = element.dataset.liveRegion;
      const matchingElements = document.querySelectorAll(`[data-live-region="${liveRegionValue}"]`);
      
      // Extract the filter content from the source element
      // This works for both inline (dropdown) and offcanvas (accordion-body) structures
      let sourceFilterContent = null;
      
      // Try to find content in dropdown first (inline structure)
      const sourceDropdownContent = element.querySelector('.facet-dropdown-menu .p-3:last-child');
      if (sourceDropdownContent) {
        sourceFilterContent = sourceDropdownContent.innerHTML;
      } else {
        // No dropdown - this is likely offcanvas structure or direct content
        // Check if it's an accordion-body
        if (element.classList.contains('accordion-body')) {
          sourceFilterContent = element.innerHTML;
        } else {
          // Might be inline structure but dropdown not found - extract filter content only
          const formChecks = element.querySelectorAll('.form-check');
          const rowContent = element.querySelectorAll('.row.g-3');
          
          if (formChecks.length > 0) {
            sourceFilterContent = Array.from(formChecks).map(el => el.outerHTML).join('');
          } else if (rowContent.length > 0) {
            sourceFilterContent = Array.from(rowContent).map(el => el.outerHTML).join('');
          } else {
            // Last resort: use innerHTML but remove button-related elements
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = element.innerHTML;
            tempDiv.querySelectorAll('.btn-group, .btn, .dropdown-menu, button').forEach(el => el.remove());
            sourceFilterContent = tempDiv.innerHTML;
          }
        }
      }
      
      if (!sourceFilterContent) return;
      
      // Update all matching target elements
      matchingElements.forEach((targetElement) => {
        // Check if this is an inline facet (has dropdown menu structure)
        const dropdownContent = targetElement.querySelector('.facet-dropdown-menu .p-3:last-child');
        
        if (dropdownContent) {
          // INLINE STRUCTURE: Update only the dropdown content area
          dropdownContent.innerHTML = sourceFilterContent;
        } else {
          // OFFCANVAS STRUCTURE: Update the accordion-body directly
          targetElement.innerHTML = sourceFilterContent;
        }
      });
    });

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);
  }

  /**
   * Extract and replace the active facet (filter badges) HTML
   * 
   * @param {String} html
   */
  renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector('#facets-active');
    if (!activeFacetsElement) return;

    // Extract the "Clear all" badge (rounded-pill) and filter badges (rounded-1) from source
    // The "Clear all" badge should always be present in the template
    let clearAllBadge = null;
    let clearAllBadgeHTML = '';
    
    // Try to find the "Clear all" badge in multiple ways
    // First, try in any container
    const sourceContainer = activeFacetsElement.querySelector('[class*="container"]');
    if (sourceContainer) {
      clearAllBadge = sourceContainer.querySelector('.badge.rounded-pill');
    }
    
    // If not found, try directly in activeFacetsElement
    if (!clearAllBadge) {
      clearAllBadge = activeFacetsElement.querySelector('.badge.rounded-pill');
    }
    
    // If still not found, try finding by the link text or href pattern
    if (!clearAllBadge) {
      const allBadges = activeFacetsElement.querySelectorAll('.badge');
      clearAllBadge = Array.from(allBadges).find(badge => {
        const link = badge.querySelector('a.facet-remove');
        if (link) {
          const href = link.getAttribute('href') || '';
          const text = link.textContent.toLowerCase().trim();
          // Look for "clear all" link or link that goes to base results URL
          return text.includes('clear') || (!href.includes('filter') && !href.includes('sort_by'));
        }
        return false;
      });
    }
    
    clearAllBadgeHTML = clearAllBadge ? clearAllBadge.outerHTML : '';
    
    // Extract just the filter badge content (badges with rounded-1 class)
    const filterBadges = activeFacetsElement.querySelectorAll('.badge.rounded-1');
    const filterBadgesHTML = Array.from(filterBadges).map(b => b.outerHTML).join('');
    const hasActiveFilters = filterBadgesHTML.trim().length > 0;

    // Update ALL elements with #facets-active (for both inline and offcanvas)
    const matchingElements = document.querySelectorAll('#facets-active');
    matchingElements.forEach((targetElement) => {
      // Check the structure of each target to update appropriately
      const targetContainer = targetElement.querySelector('[class*="container"]');
      
      if (targetContainer) {
        // Inline structure: update the container's innerHTML with "Clear all" badge + filter badges
        // Always include "Clear all" badge - prioritize source (has updated URL), fallback to target
        let finalClearAllBadgeHTML = clearAllBadgeHTML;
        if (!finalClearAllBadgeHTML) {
          // If source doesn't have it, preserve it from target (shouldn't happen, but safety net)
          const existingClearAll = targetContainer.querySelector('.badge.rounded-pill');
          if (existingClearAll) {
            finalClearAllBadgeHTML = existingClearAll.outerHTML;
          }
        }
        
        // Update container content: "Clear all" badge + filter badges
        // Always include clear all badge if we have it (either from source or target)
        targetContainer.innerHTML = finalClearAllBadgeHTML + filterBadgesHTML;
      } else {
        // Offcanvas structure: update directly (no "Clear all" badge here, just filter badges)
        targetElement.innerHTML = filterBadgesHTML;
      }
      
      // Toggle visibility based on whether there are active filters
      if (hasActiveFilters) {
        targetElement.classList.remove('d-none');
      } else {
        // Hide only if there are no active filters
        targetElement.classList.add('d-none');
      }
    });
    //FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer(html) {
    document.getElementById('results-grid').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('results-grid').innerHTML;
  }

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  }

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    //this.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

export default Facets;